import { AlertTriangle, Check, Loader2, Send, Smartphone, Wallet } from 'lucide-react';
import type { PowerOutstanding } from '../lib/types';

/**
 * A token purchase, watched from the keypad to the landlord's handset.
 *
 * ## Why a purchase needs four stages and not a status
 *
 * Buying a KPLC token is four separate waits with a different failure at each
 * one, and `status` collapses them into a word. Somebody standing over a
 * handset wants to know which of the four they are in, because the remedy is
 * different every time: key it in again, wait, forward the message by hand, or
 * ring the landlord.
 *
 *   1. **Keyed in** — the purchase exists; somebody keys it into the SIM
 *      toolkit. Nothing automatic happens here and nothing can: no web page
 *      can open an M-Pesa PIN prompt, and automating a PIN would be wrong even
 *      if it could.
 *   2. **Payment confirmed** — Safaricom's message arrives and is matched, so
 *      the money provably left and the receipt is on the record.
 *   3. **Token received** — KPLC answers, the twenty digits are parsed and
 *      checked against that payment.
 *   4. **Sent to the landlord** — the digits reach the phone that needs them.
 *
 * ## Why the token itself is not displayed
 *
 * It is a bearer credential for somebody else's electricity, and this panel
 * refreshes every few seconds on a screen that may be in an office with other
 * people in it. The landlord has the number; nobody here needs to read it.
 *
 * ## Why every stage carries its evidence
 *
 * A tick that says "done" and nothing else is worth very little when somebody
 * is asking whether the money actually went. A receipt number, a time, and the
 * phone number it was sent to are checkable against the handset in their hand.
 */

type Stage = {
  key: string;
  label: string;
  icon: typeof Wallet;
  done: boolean;
  /** What happened, once it has. */
  detail: string | null;
  /** What is being waited for, while it is. */
  waiting: string;
};

function stagesOf(row: PowerOutstanding): Stage[] {
  const when = (iso: string | null): string =>
    iso
      ? new Intl.DateTimeFormat('en-KE', {
          hour: 'numeric',
          minute: '2-digit',
          timeZone: 'Africa/Nairobi',
        }).format(new Date(iso))
      : '';

  return [
    {
      key: 'requested',
      label: 'Keyed in',
      icon: Smartphone,
      done: row.stages.requested.done,
      detail: `${row.amount} to paybill 888880, account ${row.meterNumber.replace(/\s/g, '')}`,
      waiting: 'Key the payment into the handset',
    },
    {
      key: 'paid',
      label: 'Payment confirmed',
      icon: Wallet,
      done: row.stages.paid.done,
      detail: row.stages.paid.receipt
        ? `Receipt ${row.stages.paid.receipt} · ${when(row.stages.paid.at)}`
        : row.stages.paid.done
          ? `Confirmed ${when(row.stages.paid.at)}`
          : null,
      /*
       * Named, when the number is known. "Waiting for a message" is a shrug;
       * "waiting for a message from 0712…" is something somebody can act on —
       * walk to that handset and look at it.
       */
      /*
       * Two different waits, and telling them apart is the point of raising a
       * prompt at all. "Go and pay" and "the phone in your hand is buzzing,
       * type your PIN" are not the same instruction.
       */
      waiting: row.stages.paid.prompted
        ? row.payerPhone
          ? `Prompt sent to ${row.payerPhone} — enter the M-Pesa PIN on that handset`
          : 'Prompt sent — enter the M-Pesa PIN on the paying handset'
        : row.payerPhone
          ? `Waiting for the M-Pesa message from ${row.payerPhone}`
          : 'Waiting for the M-Pesa message from the handset',
    },
    {
      key: 'token',
      label: 'Token received',
      icon: Check,
      done: row.stages.token.done,
      detail: row.stages.token.done
        ? [
            row.stages.token.source === 'sms' ? 'Forwarded automatically' : 'Entered by hand',
            row.stages.token.units === null ? null : `${row.stages.token.units} kWh`,
            when(row.stages.token.at),
          ]
            .filter(Boolean)
            .join(' · ')
        : null,
      waiting: row.payerPhone
        ? `Waiting for the KPLC message on ${row.payerPhone}`
        : 'Waiting for the KPLC message',
    },
    {
      key: 'sent',
      label: 'Sent to the landlord',
      icon: Send,
      done: row.stages.sent.done,
      detail: row.stages.sent.failed
        ? `Would not send to ${row.stages.sent.to}`
        : row.stages.sent.done
          ? `${row.stages.sent.to} · ${when(row.stages.sent.at)}`
          : row.stages.sent.queued
            ? `Queued for ${row.stages.sent.to}`
            : null,
      waiting: `Will go to ${row.stages.sent.to}`,
    },
  ];
}

export function TokenProgress({
  row,
  /**
   * Whether an SMS can actually leave the building.
   *
   * The mock provider succeeds, so the outbox row reads SENT and this
   * component's last tick would go green for a message nobody received. A
   * green tick for something that did not happen is worse than a red cross:
   * the red cross gets investigated.
   */
  canDeliver = true,
}: {
  row: PowerOutstanding;
  canDeliver?: boolean;
}) {
  const stages = stagesOf(row);
  /*
   * The first stage not yet crossed is the one being waited on. Everything
   * after it is simply not started — drawn, but quiet, so the eye lands on the
   * one line that is actually happening.
   */
  const activeIndex = stages.findIndex((stage) => !stage.done);
  const sendFailed = row.stages.sent.failed;
  /* Recorded as sent, by a provider that sends nothing. */
  const pretendSent = !canDeliver && row.stages.sent.done;

  return (
    <ol className="mt-3 space-y-1.5" aria-label={`Progress for ${row.meterLabel}`}>
      {stages.map((stage, index) => {
        const active = index === activeIndex;
        const failedHere = stage.key === 'sent' && (sendFailed || pretendSent);
        const Icon = failedHere ? AlertTriangle : stage.done ? Check : stage.icon;

        return (
          <li key={stage.key} className="flex items-start gap-2.5">
            <span
              className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                failedHere
                  ? 'bg-danger-100 text-danger-700'
                  : stage.done
                    ? 'bg-secondary-600 text-white'
                    : active
                      ? 'bg-primary-500 text-white'
                      : 'bg-muted text-muted-foreground'
              }`}
            >
              {active && !stage.done && !failedHere ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              )}
            </span>

            <span className="min-w-0 flex-1">
              <span
                className={`block text-sm ${
                  stage.done || active ? 'font-medium' : 'text-muted-foreground'
                }`}
              >
                {stage.label}
              </span>
              <span
                className={`block text-xs ${
                  failedHere ? 'text-danger-700' : 'text-muted-foreground'
                }`}
              >
                {stage.key === 'sent' && pretendSent
                  ? 'Recorded, but not delivered — no SMS provider is set up'
                  : stage.done || failedHere
                    ? stage.detail
                    : active
                      ? stage.waiting
                      : stage.detail}
              </span>
            </span>
          </li>
        );
      })}

      {/*
        * What went wrong with a forwarded message, where it happened.
        *
        * `failureReason` is written when a message arrived and did not fit —
        * wrong amount, too old, a token already sent. It is the difference
        * between "still waiting" and "something came and was refused", and
        * without it the panel would sit on stage three looking patient while
        * the answer had already arrived and been turned away.
        */}
      {row.failureReason && (
        <li className="flex items-start gap-2.5 rounded-xl bg-accent-50 p-2.5">
          <AlertTriangle
            className="mt-0.5 h-4 w-4 shrink-0 text-accent-700"
            aria-hidden="true"
          />
          <span className="text-xs text-accent-900">{row.failureReason}</span>
        </li>
      )}
    </ol>
  );
}
